import React, { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { Box, Stack, Button, Heading, Text, Input, Checkbox, Card } from '@/components/lib'
import api from '../../../api'
import utils from '@/utils/utils'
import useStore from '../../../store'

function Settings (props) {
  const { trial } = props
  const [newPassword, setNewPassword] = useState('')
  const [defaultNotificationTitle, setDefaultNotificationTitle] = useState('')
  const { user, updateTrial, addEmailSubscription, removeEmailSubscription } = useStore()
  const canManage = utils.canManageTrial(user, trial)

  useEffect(() => {
    if (props.trial.password) setNewPassword('password')
    if (props.trial.defaultNotificationTitle) setDefaultNotificationTitle(props.trial.defaultNotificationTitle)
  }, [props.trial.password, props.trial.defaultNotificationTitle])

  function updateRestrictRegistration (enabled) {
    api.trials.update(trial._id, { restrictRegistration: !enabled }, t => updateTrial(t._id, t), err => utils.toast('error', err.message))
  }

  function updateEnableLookback (enabled) {
    api.trials.update(trial._id, { enableLookback: enabled }, t => updateTrial(t._id, t), err => utils.toast('error', err.message))
  }

  function saveDefaultNotificationTitle () {
    api.trials.update(props.trial._id, { defaultNotificationTitle }, (t) => {
      updateTrial(t._id, t)
      utils.toast('success', 'Default notification title updated')
    }, err => utils.toast('error', err.message))
  }

  function savePassword () {
    if (newPassword === 'password') return utils.toast('error', 'Password has not changed or is too weak.')
    api.trials.update(props.trial._id, { password: newPassword }, (t) => {
      updateTrial(t._id, t)
      utils.toast('success', 'Password updated')
    }, err => utils.toast('error', err.message))
  }

  function subscribe (subscriptionTopic, enabled) {
    const f = enabled ? api.users.createEmailSubscription : api.users.deleteEmailSubscription
    f(user._id, subscriptionTopic, subData => {
      utils.toast('success', 'Successfully updated subscription')
      if (enabled) addEmailSubscription(subscriptionTopic)
      else removeEmailSubscription(subscriptionTopic)
    }, err => utils.toast('error', err.message))
  }

  const subscriptionTopics = [
    `trial.${trial._id}.dailyUpdates`,
    `trial.${trial._id}.weeklyUpdates`
  ]

  return (
    <Stack gap={5}>
      <Card variant='outline' gap={2}>
        <Heading as='h3' size='md'>Periodic email updates</Heading>
        <Text>Get daily or weekly email updates about your trial.</Text>
        {subscriptionTopics.map((topic, i) => {
          const enabled = user?.subscriptions?.email?.includes(topic)
          return (
            <Box key={i}>
              <Checkbox isChecked={enabled} onChange={e => subscribe(topic, e.checked)}>Subscribe to {topic.includes('daily') ? 'daily' : 'weekly'} updates</Checkbox>
            </Box>
          )
        })}
      </Card>

      <Card variant='outline' gap={2}>
        <Heading as='h3' size='md'>Open registration</Heading>
        <Checkbox isChecked={!trial.restrictRegistration} disabled={!canManage} onChange={e => updateRestrictRegistration(e.checked)}>Enable open registration</Checkbox>
        <Text>When Open Registration is enabled, participants will be able to join the trial using any Participant ID, and they will be auto-registered after they first login with this Participant ID.</Text>
        <Text>If you disable this setting, you will need to pre-register Participant IDs on the <Link to={`/trials/${trial._id}/participants`}>Participants page</Link> to determine which IDs can be used to access the trial.</Text>
      </Card>

      <Card variant='outline' gap={2}>
        <Heading as='h3' size='md'>Participant look-back</Heading>
        <Checkbox isChecked={trial.enableLookback} disabled={!canManage} onChange={e => updateEnableLookback(e.checked)}>Enable look-back</Checkbox>
        <Text>When enabled, participants can be shown previously-inputted values for numeric fields and sliders. In some cases, this may allow participants to make better comparisons over time.</Text>
      </Card>

      <Card variant='outline' gap={2}>
        <Heading as='h3' size='md'>Response sign-off note</Heading>
        <Checkbox isChecked={trial.signOffNoteRequired} disabled={!canManage} onChange={e => api.trials.update(trial._id, { signOffNoteRequired: e.checked }, t => updateTrial(t._id, t), err => utils.toast('error', err.message))}>Always require sign-off note</Checkbox>
        <Text>Sign-off notes are provided by users when submitting data about a participant. By default they are only required when overwriting or changing existing content, but when this setting is enabled the sign-off note must always be provided.</Text>
      </Card>

      <Card variant='outline' gap={2}>
        <Heading as='h3' size='md'>Default notification title</Heading>
        <Text>Set the default title for notifications sent to participants. You can sometimes override this title when sending individual notifications.</Text>
        <Input placeholder='Message from trial admin' value={defaultNotificationTitle} disabled={!canManage} onChange={e => setDefaultNotificationTitle(e.target.value)} />
        {canManage &&
          <Box>
            <Button colorPalette='primary' onClick={saveDefaultNotificationTitle}>Save default title</Button>
          </Box>}
      </Card>

      <Card variant='outline' gap={2}>
        <Heading as='h3' size='md'>Trial password</Heading>
        <Text>Optionally set a password that participants will need in addition to their Participant ID to access the trial. If you leave it blank, participants can login without a password (unless one has been assigned to them individually).</Text>
        <Input type='password' placeholder='Trial password' value={newPassword} disabled={!canManage} onChange={e => setNewPassword(e.target.value)} />
        {canManage &&
          <Box>
            <Button colorPalette='primary' onClick={savePassword}>Save password</Button>
          </Box>}
      </Card>
    </Stack>
  )
}

export default Settings
